import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { authorizationService } from '../api/services/authorization.service';
import { 
  AuthorizationState, 
  RolePrivilegeDto,
  PrivilegeName
} from '../api/types/dtos';

interface AuthorizationContextType extends AuthorizationState {
  activePrivileges: Set<string>;
  
  // Permission checking methods
  hasPrivilege: (privilege: PrivilegeName | string) => boolean;
  hasAnyPrivilege: (privileges: PrivilegeName[] | string[]) => boolean;
  hasAllPrivileges: (privileges: PrivilegeName[] | string[]) => boolean;
  hasRole: (roleName: string) => boolean;
  hasAnyRole: (roleNames: string[]) => boolean;
  
  // Authorization management
  refreshAuthorization: () => Promise<void>;
  clearAuthorization: () => void;
  
  // Utility methods
  getActivePrivileges: () => string[];
  getActiveRoles: () => string[];
  getAuthorizationDebugInfo: () => any;
}

const AuthorizationContext = createContext<AuthorizationContextType>({
  // Default state
  userRoles: [],
  rolePrivileges: {},
  activePrivileges: new Set(),
  loading: true,
  error: null,
  lastUpdated: null,
  
  // Default methods
  hasPrivilege: () => false,
  hasAnyPrivilege: () => false,
  hasAllPrivileges: () => false,
  hasRole: () => false,
  hasAnyRole: () => false,
  refreshAuthorization: async () => {},
  clearAuthorization: () => {},
  getActivePrivileges: () => [],
  getActiveRoles: () => [],
  getAuthorizationDebugInfo: () => ({}),
});

export const useAuthorization = () => useContext(AuthorizationContext);

interface AuthorizationProviderProps {
  children: ReactNode;
}

export function AuthorizationProvider({ children }: AuthorizationProviderProps) {
  const { user, isAuthenticated, isSessionReady } = useAuth();
  const { selectedOrganization } = useOrganization();
  
  const [state, setState] = useState<AuthorizationState & { activePrivileges: Set<string> }>({
    userRoles: [],
    rolePrivileges: {},
    activePrivileges: new Set(),
    loading: true,
    error: null,
    lastUpdated: null,
  });

  // Track pending authorization requests to prevent race conditions
  const [pendingOrganizationId, setPendingOrganizationId] = useState<string | null>(null);
  
  // Track state changes for debugging
  const [stateChangeLog, setStateChangeLog] = useState<any[]>([]);

  // Build active privileges set from role privileges
  const buildActivePrivileges = useCallback((rolePrivileges: Record<string, RolePrivilegeDto[]>) => {
    const privileges = new Set<string>();
    
    Object.values(rolePrivileges).forEach(privilegeList => {
      privilegeList.forEach(rolePrivilege => {
        if (rolePrivilege.isActive && rolePrivilege.privilege.isActive) {
          privileges.add(rolePrivilege.privilege.name);
        }
      });
    });
    
    return privileges;
  }, []);

  // Fetch authorization data for the current organization
  const fetchAuthorization = useCallback(async (organizationId: string) => {
    const logEntry = {
      timestamp: new Date().toISOString(),
      action: 'fetchAuthorization',
      organizationId,
      pendingOrgId: pendingOrganizationId,
    };
    
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      
      const { userRoles, rolePrivileges } = await authorizationService.getOrganizationPrivileges(organizationId, user.id);
      const activePrivileges = buildActivePrivileges(rolePrivileges);
      
      const newState = {
        userRoles,
        rolePrivileges,
        activePrivileges,
        loading: false,
        error: null,
        lastUpdated: new Date().toISOString(),
      };
      
      setState(newState);
      
      // Log state change
      setStateChangeLog(prev => [
        { ...logEntry, result: 'success', newState: { ...newState, activePrivileges: Array.from(activePrivileges) } },
        ...prev.slice(0, 9)
      ]);
      
    } catch (error) {
      // Handle specific error cases
      let errorMessage = (error as Error).message;
      let errorType = 'unknown';
      
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.status === 403) {
          errorMessage = 'Usuario no tiene permisos para acceder a información de roles y privilegios';
          errorType = 'forbidden';
        } else if (axiosError.response?.status === 401) {
          errorMessage = 'Sesión expirada. Por favor, inicia sesión nuevamente';
          errorType = 'unauthorized';
        } else if (axiosError.response?.status === 404) {
          errorMessage = 'Endpoint de autorización no encontrado';
          errorType = 'not_found';
        }
      }
      
      const errorState = {
        loading: false,
        error: errorMessage,
        lastUpdated: new Date().toISOString(),
        userRoles: [],
        rolePrivileges: {},
        activePrivileges: new Set<string>(),
      };
      
      setState(prev => ({ ...prev, ...errorState }));
      
      // Log error with type
      setStateChangeLog(prev => [
        { ...logEntry, result: 'error', error: errorMessage, errorType },
        ...prev.slice(0, 9)
      ]);
    }
  }, [buildActivePrivileges, pendingOrganizationId]);

  // Permission checking methods
  const hasPrivilege = useCallback((privilege: PrivilegeName | string): boolean => {
   return state.activePrivileges.has(privilege);
  }, [state.activePrivileges, selectedOrganization?.id]);

  const hasAnyPrivilege = useCallback((privileges: PrivilegeName[] | string[]): boolean => {
    return privileges.some(privilege => state.activePrivileges.has(privilege));
  }, [state.activePrivileges]);

  const hasAllPrivileges = useCallback((privileges: PrivilegeName[] | string[]): boolean => {
    return privileges.every(privilege => state.activePrivileges.has(privilege));
  }, [state.activePrivileges]);

  const hasRole = useCallback((roleName: string): boolean => {
    return state.userRoles.some(userRole => 
      userRole.isActive && userRole.roleId === roleName
    );
  }, [state.userRoles]);

  const hasAnyRole = useCallback((roleNames: string[]): boolean => {
    return state.userRoles.some(userRole => 
      userRole.isActive && roleNames.includes(userRole.roleId)
    );
  }, [state.userRoles]);

  const refreshAuthorization = useCallback(async () => {
    if (selectedOrganization?.id) {
      await fetchAuthorization(selectedOrganization.id);
    }
  }, [selectedOrganization?.id, fetchAuthorization]);

  const clearAuthorization = useCallback(() => {
    setState({
      userRoles: [],
      rolePrivileges: {},
      activePrivileges: new Set(),
      loading: false,
      error: null,
      lastUpdated: null,
    });
  }, []);

  const getActivePrivileges = useCallback(() => {
    return Array.from(state.activePrivileges);
  }, [state.activePrivileges]);

  const getActiveRoles = useCallback(() => {
      return state.userRoles
      .filter(userRole => userRole.isActive)
      .map(userRole => userRole.roleId);
  }, [state.userRoles]);

  // Debug method to get authorization state info
  const getAuthorizationDebugInfo = useCallback(() => {
    return {
      currentOrganization: selectedOrganization ? {
        id: selectedOrganization.id,
        name: selectedOrganization.name
      } : null,
      pendingOrganizationId,
      userRoles: state.userRoles.map(r => ({
        roleId: r.roleId,
        isActive: r.isActive
      })),
      activePrivileges: Array.from(state.activePrivileges),
      loading: state.loading,
      lastUpdated: state.lastUpdated,
      error: state.error,
      stateChangeLog: stateChangeLog.slice(0, 5) // Last 5 changes
    };
  }, [selectedOrganization, pendingOrganizationId, state, stateChangeLog]);

  // Effect to fetch authorization when organization changes with debounce
  useEffect(() => {
    if (isAuthenticated && isSessionReady && selectedOrganization?.id && user?.id) {
      // Set pending organization to prevent race conditions
      setPendingOrganizationId(selectedOrganization.id);
      
      // Log organization change
      setStateChangeLog(prev => [
        {
          timestamp: new Date().toISOString(),
          action: 'organizationChange',
          organizationId: selectedOrganization.id,
          organizationName: selectedOrganization.name,
          isAuthenticated,
          isSessionReady
        },
        ...prev.slice(0, 9)
      ]);
      
      // Debounce the authorization fetch to prevent rapid changes
      const timeoutId = setTimeout(() => {
        fetchAuthorization(selectedOrganization.id);
        setPendingOrganizationId(null);
      }, 150); // 150ms debounce
      
      return () => {
        clearTimeout(timeoutId);
        setPendingOrganizationId(null);
      };
    } else if (!isAuthenticated) {
      // User is not authenticated - clear everything
      clearAuthorization();
      setPendingOrganizationId(null);
    } else if (!selectedOrganization?.id) {
      // Authenticated but org not yet selected (e.g. page reload) - keep loading
      setState(prev => ({ ...prev, loading: true }));
      setPendingOrganizationId(null);
    }
  }, [isAuthenticated, isSessionReady, selectedOrganization?.id, user?.id]);

  const contextValue: AuthorizationContextType = {
    ...state,
    hasPrivilege,
    hasAnyPrivilege,
    hasAllPrivileges,
    hasRole,
    hasAnyRole,
    refreshAuthorization,
    clearAuthorization,
    getActivePrivileges,
    getActiveRoles,
    getAuthorizationDebugInfo,
  };

  return (
    <AuthorizationContext.Provider value={contextValue}>
      {children}
    </AuthorizationContext.Provider>
  );
}
